import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import axios from "axios"
import { ArrowUpRight, Plus, CalendarPlus, Sparkles, ChevronRight, Volume2 } from "lucide-react"
import { LineChart, Line, ResponsiveContainer, Tooltip } from "recharts"
import AppLayout from "@/components/app/AppLayout"
import { Button } from "@/components/ui/button"
import { useAuth } from "@/components/auth-provider"
import { formatRon } from "@/lib/format"
import { API_BASE } from "@/config/api"
import { toast } from "sonner"
import { OnboardingWizard } from "@/components/app/OnboardingWizard"
import { SortableWidgets } from "@/components/app/SortableWidgets"

const API = API_BASE

export default function Dashboard() {
  const { t } = useTranslation()
  const [stats, setStats] = useState(null)
  const [copilot, setCopilot] = useState([])
  const [anomalies, setAnomalies] = useState([])
  const [extra, setExtra] = useState(null)
  const [showOnboarding, setShowOnboarding] = useState(
    () => !localStorage.getItem("asistentpersonal-onboarding-done")
  )
  const [therapist, setTherapist] = useState(null)
  const [widgetOrder, setWidgetOrder] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("dashboard-widgets") || '["metrics","chart","copilot","today"]')
    } catch {
      return ["metrics", "chart", "copilot", "today"]
    }
  })
  const { user } = useAuth()
  const navigate = useNavigate()

  useEffect(() => {
    axios.get(`${API}/dashboard`).then((r) => setStats(r.data)).catch(() => {})
    axios.get(`${API}/dashboard/copilot`).then((r) => setCopilot(r.data.items || [])).catch(() => {})
    axios.get(`${API}/dashboard/metrics`).then((r) => setExtra(r.data)).catch(() => {})
    axios
      .get(`${API}/analytics/dashboard`, { params: { days: 90 } })
      .then((r) => setAnomalies(r.data?.anomalies || []))
      .catch(() => {})
    axios.get(`${API}/settings`).then((r) => {
      if (r.data.dashboard_layout?.length) setWidgetOrder(r.data.dashboard_layout)
    }).catch(() => {})
    axios.get(`${API}/dashboard/therapist-metrics`).then((r) => setTherapist(r.data)).catch(() => {})
  }, [])

  const persistWidgets = (next) => {
    setWidgetOrder(next)
    localStorage.setItem("dashboard-widgets", JSON.stringify(next))
    axios.patch(`${API}/settings`, { dashboard_layout: next }).catch(() => {})
  }

  const runCopilotAction = (action, e) => {
    e?.stopPropagation()
    if (action.type === "whatsapp" || action.type === "navigate") window.open(action.url?.startsWith("http") ? action.url : action.url, "_blank")
    else if (action.url) navigate(action.url)
  }

  const metrics = stats
    ? [
        {
          label: t("app.dashboard.metrics.appointmentsToday"),
          value: stats.appointments_today,
          delta: t("app.dashboard.metrics.deltaVsYesterday", {
            delta: `${stats.appointments_delta >= 0 ? "+" : ""}${stats.appointments_delta}`,
          }),
        },
        {
          label: t("app.dashboard.metrics.revenueMonth"),
          value: formatRon(stats.revenue_month),
          delta: t("app.dashboard.metrics.deltaMom", {
            pct: `${stats.revenue_mom_pct >= 0 ? "+" : ""}${stats.revenue_mom_pct}`,
          }),
        },
        {
          label: t("app.dashboard.metrics.newClients"),
          value: stats.new_clients_month,
          delta: t("app.dashboard.metrics.statusNew"),
        },
        {
          label: t("app.dashboard.metrics.notesToSign"),
          value: stats.unread_notes,
          delta: t("app.dashboard.metrics.draft"),
        },
      ]
    : Array.from({ length: 4 }).map(() => ({
        label: t("app.dashboard.metrics.placeholder"),
        value: t("app.dashboard.metrics.placeholder"),
        delta: t("app.dashboard.metrics.placeholder"),
      }))

  const firstName = (() => {
    const parts = (user?.name || "").split(" ").filter(Boolean)
    if (!parts.length) return t("app.dashboard.greetingFallback")
    const titles = ["dr.", "dr", "mr.", "mrs.", "ms.", "miss"]
    return titles.includes(parts[0].toLowerCase()) && parts.length > 1 ? parts[1] : parts[0]
  })()

  const morningBrief = async () => {
    try {
      const { data } = await axios.post(`${API}/ai/dashboard/morning-brief`)
      if ("speechSynthesis" in window) {
        const u = new SpeechSynthesisUtterance(data.text)
        u.lang = "ro-RO"
        speechSynthesis.speak(u)
      }
      toast.success(data.text)
    } catch {
      toast.error(t("app.dashboard.toastBriefFailed"))
    }
  }

  const metricsBlock = (
    <>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="dashboard-metrics">
        {metrics.map((m, i) => (
          <div key={i} className="rounded-2xl border border-border bg-card p-5">
            <p className="text-xs uppercase tracking-[0.18em] font-semibold text-muted-foreground">{m.label}</p>
            <p className="font-display text-3xl tracking-tight font-medium mt-3">{m.value}</p>
            <p className="text-xs text-muted-foreground mt-1.5">{m.delta}</p>
          </div>
        ))}
      </div>
      {extra && (
        <div className="grid grid-cols-3 gap-4 mb-6 text-sm">
          <div className="rounded-xl border border-border bg-card p-4">
            <p className="text-muted-foreground text-xs">{t("app.dashboard.extra.sessionsMonth")}</p>
            <p className="font-medium mt-1">{extra.sessions_month}</p>
          </div>
          <div className="rounded-xl border border-border bg-card p-4">
            <p className="text-muted-foreground text-xs">{t("app.dashboard.extra.noShowRate")}</p>
            <p className="font-medium mt-1">{extra.no_show_rate}%</p>
          </div>
          <div className="rounded-xl border border-border bg-card p-4">
            <p className="text-muted-foreground text-xs">{t("app.dashboard.extra.currency")}</p>
            <p className="font-medium mt-1">{extra.currency}</p>
          </div>
        </div>
      )}
    </>
  )

  const chartBlock = (
    <div className="rounded-2xl border border-border bg-card p-6 mb-6">
      <div className="flex items-center justify-between mb-2">
        <div>
          <p className="text-xs uppercase tracking-[0.18em] font-semibold text-muted-foreground">{t("app.dashboard.revenueTrend")}</p>
          <p className="font-display text-2xl tracking-tight font-medium mt-1">
            {stats ? formatRon(stats.revenue_month) : t("app.dashboard.metrics.placeholder")}{" "}
            <span className="text-sm text-muted-foreground font-normal">{t("app.dashboard.thisMonth")}</span>
          </p>
        </div>
        <span className="text-xs px-2.5 py-1 rounded-full bg-secondary text-secondary-foreground">{t("app.dashboard.weeks12")}</span>
      </div>
      <div className="h-56 -mx-2 mt-2" style={{ minHeight: 200 }}>
        <ResponsiveContainer width="100%" height={220}>
          <LineChart data={stats?.revenue_series ?? []}>
            <Tooltip
              contentStyle={{
                background: "hsl(var(--card))",
                border: "1px solid hsl(var(--border))",
                borderRadius: 12,
                fontSize: 12,
              }}
              formatter={(v) => [formatRon(v), t("app.dashboard.revenueTooltip")]}
            />
            <Line type="monotone" dataKey="amount" stroke="hsl(var(--accent))" strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  )

  const copilotBlock = (
    <div className="rounded-2xl border border-border bg-card p-6 mb-6">
      <div className="flex items-center gap-2 mb-4">
        <Sparkles strokeWidth={1.5} className="h-4 w-4 text-accent" />
        <p className="text-xs uppercase tracking-[0.18em] font-semibold text-muted-foreground">{t("app.dashboard.copilotTitle")}</p>
      </div>
      <ul className="space-y-3">
        {copilot.length ? copilot.map((item, i) => (
          <li
            key={i}
            className="flex flex-col gap-2 p-3 rounded-lg hover:bg-muted/60 transition-colors text-sm"
            data-testid={`copilot-item-${i}`}
          >
            <div className="flex items-start gap-3 cursor-pointer" onClick={() => item.action_url && navigate(item.action_url)}>
              <span className="h-1.5 w-1.5 rounded-full bg-accent mt-2 shrink-0 block" />
              <p className="flex-1">{item.title}</p>
              <ChevronRight strokeWidth={1.5} className="h-4 w-4 text-muted-foreground" />
            </div>
            {item.actions?.length > 0 && (
              <div className="flex gap-2 pl-5">
                {item.actions.map((a, j) => (
                  <Button key={j} size="sm" variant="outline" className="h-7 text-xs rounded-full" onClick={(e) => runCopilotAction(a, e)}>
                    {a.label}
                  </Button>
                ))}
              </div>
            )}
          </li>
        )) : (
          <li className="text-sm text-muted-foreground p-3">{t("app.dashboard.copilotEmpty")}</li>
        )}
      </ul>
    </div>
  )

  const todayBlock = (
    <div className="rounded-2xl border border-border bg-card overflow-hidden mb-6">
      <div className="p-6 flex items-center justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.18em] font-semibold text-muted-foreground">{t("app.dashboard.scheduleToday")}</p>
          <p className="font-display text-2xl tracking-tight font-medium mt-1">{t("app.dashboard.sessionsCount", { count: stats?.appointments_today ?? 0 })}</p>
        </div>
        <Button variant="ghost" size="sm" className="rounded-full" onClick={() => navigate("/app/appointments")} data-testid="db-view-schedule">
          {t("app.dashboard.viewCalendar")} <ArrowUpRight strokeWidth={1.5} className="ml-1 h-3.5 w-3.5" />
        </Button>
      </div>
      <div className="divide-y divide-border" data-testid="db-today-list">
        {(stats?.appointments_today_list ?? []).map((a) => (
          <div key={a.id} className="px-6 py-4 flex items-center gap-4 hover:bg-muted/40 transition-colors cursor-pointer" onClick={() => navigate("/app/appointments")}>
            <div className="w-20 text-sm font-mono text-muted-foreground">
              {new Date(a.start).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
            </div>
            <div className="h-9 w-9 rounded-full bg-secondary grid place-items-center text-sm font-medium">
              {a.client_name.split(" ").map((p) => p[0]).slice(0, 2).join("")}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium truncate">{a.client_name}</p>
              <p className="text-xs text-muted-foreground">{a.type} · {a.location}</p>
            </div>
            <span className={`text-xs px-2.5 py-1 rounded-full ${a.status === "confirmed" ? "bg-secondary" : "bg-accent/15 text-accent"}`}>
              {a.status}
            </span>
          </div>
        ))}
        {!stats?.appointments_today_list?.length && (
          <div className="px-6 py-10 text-center text-sm text-muted-foreground">{t("app.dashboard.noSessionsToday")}</div>
        )}
      </div>
    </div>
  )

  const widgetMap = {
    metrics: metricsBlock,
    chart: chartBlock,
    copilot: copilotBlock,
    today: todayBlock,
  }

  return (
    <>
    {showOnboarding && (
      <OnboardingWizard onDone={() => {
        localStorage.setItem("asistentpersonal-onboarding-done", "1")
        setShowOnboarding(false)
      }} />
    )}
    <AppLayout
      title={t("app.dashboard.greeting", { name: firstName })}
      actions={
        <>
          <Button variant="outline" size="sm" className="rounded-full" onClick={() => navigate("/app/notes?action=new")} data-testid="db-quick-note">
            <Plus strokeWidth={1.5} className="h-4 w-4 mr-1" /> {t("app.dashboard.quickNote")}
          </Button>
          <Button size="sm" className="rounded-full" onClick={() => navigate("/app/appointments?action=new")} data-testid="db-new-appt">
            <CalendarPlus strokeWidth={1.5} className="h-4 w-4 mr-1" /> {t("app.dashboard.newAppointment")}
          </Button>
          <Button variant="outline" size="sm" className="rounded-full" onClick={morningBrief}>
            <Volume2 strokeWidth={1.5} className="h-4 w-4 mr-1" /> {t("app.dashboard.morningBrief")}
          </Button>
        </>
      }
    >
      <SortableWidgets order={widgetOrder} onReorder={persistWidgets} />
      {anomalies[0] && (
        <div className="mb-6 rounded-xl border border-accent/40 bg-accent/10 px-4 py-3 flex flex-wrap items-center gap-3 text-sm">
          <Sparkles className="h-4 w-4 text-accent shrink-0" strokeWidth={1.5} />
          <p className="flex-1">{anomalies[0].message}</p>
          <Button
            variant="outline"
            size="sm"
            className="rounded-full h-8"
            onClick={() => navigate(anomalies[0].action_url || "/app/analytics")}
          >
            {t("app.nav.analytics")}
          </Button>
        </div>
      )}
      {therapist && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6 text-sm">
          <div className="rounded-xl border border-border bg-card p-3">
            <p className="text-xs text-muted-foreground">{t("app.dashboard.therapist.utilization")}</p>
            <p className="font-medium">{therapist.utilization_pct}%</p>
          </div>
          <div className="rounded-xl border border-border bg-card p-3">
            <p className="text-xs text-muted-foreground">{t("app.dashboard.therapist.sessionsWeek")}</p>
            <p className="font-medium">{therapist.sessions_this_week}</p>
          </div>
          <div className={`rounded-xl border p-3 ${therapist.burnout_risk ? "border-destructive/50 bg-destructive/5" : "border-border bg-card"}`}>
            <p className="text-xs text-muted-foreground">{t("app.dashboard.therapist.burnout")}</p>
            <p className="font-medium">{therapist.burnout_risk ? t("app.dashboard.therapist.burnoutWarning") : t("app.dashboard.therapist.burnoutOk")}</p>
          </div>
          {therapist.avg_session_rating != null && (
            <div className="rounded-xl border border-border bg-card p-3">
              <p className="text-xs text-muted-foreground">{t("app.dashboard.therapist.rating")}</p>
              <p className="font-medium">{therapist.avg_session_rating}/5</p>
            </div>
          )}
        </div>
      )}
      {widgetOrder.map((id) => (
        <div key={id}>{widgetMap[id]}</div>
      ))}
    </AppLayout>
    </>
  )
}
